import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GOOGLE_G_COLORS = ['#4285F4', '#EA4335', '#FBBC05', '#34A853'];

// Initials avatar with gradient background
const Avatar = ({ name, index }) => {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const palette = [
    ['#2D8DB4', '#F7945D'],
    ['#F7945D', '#2D8DB4'],
    ['#1B5E7E', '#F7945D'],
    ['#2D8DB4', '#EA4335'],
    ['#34A853', '#2D8DB4'],
    ['#F7945D', '#4285F4']
  ];
  const [a, b] = palette[index % palette.length];
  return (
    <div
      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
      style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
};

const GoogleG = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill={GOOGLE_G_COLORS[0]} d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill={GOOGLE_G_COLORS[1]} d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill={GOOGLE_G_COLORS[3]} d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill={GOOGLE_G_COLORS[2]} d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

const GoogleReviews = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/testimonials`);
        setReviews(res.data || []);
      } catch (e) {
        setReviews([]);
      }
    };
    fetch();
  }, []);

  if (reviews.length === 0) return null;

  const avg = (reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length).toFixed(1);

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50" data-testid="google-reviews">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 bg-white px-5 py-3 rounded-full shadow-md border border-gray-100 mb-4">
            <GoogleG />
            <span className="font-semibold text-gray-800">Google Reviews</span>
            <span className="text-gray-300">|</span>
            <div className="flex items-center gap-1">
              <span className="font-bold text-lg">{avg}</span>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={14} className="fill-[#FBBC05] text-[#FBBC05]" />
                ))}
              </div>
              <span className="text-sm text-gray-500 ml-1">({reviews.length})</span>
            </div>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-3">What Our Clients Say on Google</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">Real reviews from travelers who trusted Varni Visa with their journeys</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.slice(0, 6).map((r, idx) => (
            <Card key={r.id || idx} className="hover:shadow-xl transition-shadow border border-gray-100">
              <CardContent className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <Avatar name={r.name} index={idx} />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{r.name}</div>
                    <div className="text-xs text-gray-500">{r.location}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex">
                        {[...Array(r.rating || 5)].map((_, i) => (
                          <Star key={i} size={14} className="fill-[#FBBC05] text-[#FBBC05]" />
                        ))}
                      </div>
                      <span className="text-xs text-gray-400">{r.date || ''}</span>
                    </div>
                  </div>
                  <GoogleG />
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">"{r.text}"</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-10">
          <a
            href="https://www.google.com/search?q=Varni+Visa+Surat"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 bg-white border-2 border-gray-200 rounded-full font-semibold text-gray-700 hover:border-[#2D8DB4] hover:text-[#2D8DB4] transition-colors"
          >
            <GoogleG />
            See all reviews on Google
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
